import { cn } from '../../lib/utils';

/**
 * Shared PDF download pill (CR-034/035, Frank 2026-08-28 mockups).
 *
 * Three real callers (DEC-044): `SectionNav`'s right-edge strip button,
 * the mobile drawer's footer (`DrawerDownloadLinks`), and the homepage
 * rail (`HomeRail`). Replaces the retired gold "report placeholder" link
 * placeholder now that the report is published.
 *
 * - `primary`  — solid brand-blue pill, white text (mockup: "Download Full
 *   Report"). White on `bg-blue` (#0074CC) measures 4.81:1 → WCAG AA for
 *   16px text; hover darkens to `blue-link`.
 * - `secondary` — outlined pill, dark text on white (mockup: "Download
 *   Press Release").
 *
 * PDFs open in a NEW window (Frank: "opens in a new window") — `_blank` +
 * `rel="noopener noreferrer"`. This is deliberately NOT `CEW_LINK_TARGET`
 * (`_top`), which is reserved for cew.georgetown.edu *pages* that must
 * replace the iframe (DEC-029). The new-window + PDF behaviour is announced
 * to assistive tech via a visually-hidden suffix so the accessible name is
 * "<label> (PDF, opens in a new window)" while the visible label stays
 * exactly as designed (WCAG 2.5.3 label-in-name holds). No `title`: with
 * text content it would only become a duplicate accessible description.
 *
 * Destinations are never hardcoded here — callers pass a `cew-site.ts`
 * constant (`CEW_REPORT_PDF_URL` / `CEW_PRESS_RELEASE_PDF_URL`).
 *
 * Density (designer-ship review 2026-08-30 measured every floor):
 * - `compact` (SectionNav strip): at the 1136px desktop floor the nav has
 *   1088px inside its padding and the six tabs need 870px, so a full pill
 *   (~245px) overflowed the tab row and broke its centering (e2e guard in
 *   `cew-chrome.spec.ts`). Compact drops the glyph and tightens padding
 *   below 1280px (~190px), then relaxes to the full treatment from 1280px.
 *   The mobile drawer (280px panel, 240px content) also uses it, stretched
 *   `fill`, so "Download Press Release" (~224px) fits on one line.
 * - `rail` (homepage rail): the 3/12 column is 184px wide at 1024 and
 *   ~248px at 1280, so pills are `fill` (card-width, `wrap`), `px-4`, and
 *   the glyph only appears from 1440px (288px column) where it fits without
 *   pushing "Download Press Release" (~200px of text) past the card.
 * - `default`: the full mockup treatment (glyph, `px-6`), content-width.
 */

export type PdfDownloadButtonVariant = 'primary' | 'secondary';

interface PdfDownloadButtonProps {
  readonly href: string;
  readonly label: string;
  readonly variant: PdfDownloadButtonVariant;
  /** Placement/visibility classes from the caller (e.g. the strip's
   *  `hidden min-[1136px]:inline-flex`). Never colour — that is the variant's job. */
  readonly className?: string;
  /** See the density notes in the docblock. Default `'default'`. */
  readonly density?: 'default' | 'compact' | 'rail';
  /** `fill` stretches to the container width and centres the label
   *  (drawer + rail); `content` (default) hugs the label like the mockup. */
  readonly fit?: 'content' | 'fill';
  /** Allow the label to wrap onto two lines when the container is narrower
   *  than the label (rail at 1024–~1100px). Default single-line. */
  readonly wrap?: boolean;
  readonly onClick?: () => void;
}

// Focus ring is solid brand blue (4.81:1 on white, 2px + 2px white offset)
// — the repo's older `ring-blue/40|50` convention measures ~2.1:1 against
// white, under the 3:1 non-text bar (designer-ship review 2026-08-30).
const BASE_CLASSNAME =
  'inline-flex min-h-[44px] items-center rounded-pill py-2.5 text-base font-semibold leading-none transition focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2';

const DENSITY_CLASSNAME = {
  default: 'gap-3 px-6',
  compact: 'gap-0 px-3 min-[1280px]:gap-3 min-[1280px]:px-6',
  rail: 'gap-0 px-4 min-[1440px]:gap-3',
} as const;

const GLYPH_CLASSNAME = {
  default: 'inline-block h-px w-5 shrink-0 bg-current',
  compact: 'hidden h-px w-5 shrink-0 bg-current min-[1280px]:inline-block',
  rail: 'hidden h-px w-5 shrink-0 bg-current min-[1440px]:inline-block',
} as const;

const FIT_CLASSNAME = {
  content: '',
  fill: 'w-full justify-center',
} as const;

const WRAP_CLASSNAME = {
  nowrap: 'whitespace-nowrap',
  wrap: 'whitespace-normal text-center leading-snug',
} as const;

const VARIANT_CLASSNAME: Record<PdfDownloadButtonVariant, string> = {
  primary: 'bg-blue text-white hover:bg-blue-link',
  secondary: 'border border-text-1 bg-white text-text-1 hover:bg-ice',
};

export function PdfDownloadButton({
  href,
  label,
  variant,
  className,
  density = 'default',
  fit = 'content',
  wrap = false,
  onClick,
}: PdfDownloadButtonProps) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className={cn(
        BASE_CLASSNAME,
        DENSITY_CLASSNAME[density],
        FIT_CLASSNAME[fit],
        WRAP_CLASSNAME[wrap ? 'wrap' : 'nowrap'],
        VARIANT_CLASSNAME[variant],
        className,
      )}
    >
      {/* Leading line glyph from the CEW button style — decorative only. */}
      <span aria-hidden="true" className={GLYPH_CLASSNAME[density]} />
      {label}
      <span className="sr-only"> (PDF, opens in a new window)</span>
    </a>
  );
}
